

import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Typography,
  Button,
  Paper,
  Radio,
  Stack,
  CircularProgress,
  TextField,
  Collapse,
  Chip,
  Divider,
} from "@mui/material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import PaymentIcon from "@mui/icons-material/Payment";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import CloseIcon from "@mui/icons-material/Close";
import CustomDialog from "../../CustomDialog";

const paymentOptions = [
  { 
    id: "online", 
    label: "Pay Online", 
    type: "online", 
    icon: <PaymentIcon sx={{ color: "#008080" }} />,
    description: "UPI, Cards, Net Banking, Wallets"
  },
  { 
    id: "offline", 
    label: "Pay at Clinic", 
    type: "offline", 
    icon: <LocalHospitalIcon sx={{ color: "#008080" }} />,
    description: "Pay during your visit"
  },
];

const PaymentMethodSelection = () => {
  const [selected, setSelected] = useState("online");
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogType, setDialogType] = useState("info");

  // Promo code states
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [showAvailablePromos, setShowAvailablePromos] = useState(false);
  const [availablePromos, setAvailablePromos] = useState([]);

  // Subscription states
  const [subscriptionBenefits, setSubscriptionBenefits] = useState(null);
  const [subscriptionDiscount, setSubscriptionDiscount] = useState(0);
  const [loadingSubscription, setLoadingSubscription] = useState(false);

  // API endpoints
  const bookAppointmentApi = process.env.REACT_APP_BOOK_APPOINTMENT_API;
  const verifyPaymentApi = process.env.REACT_APP_PROCESS_PAYEMENT;
  const razorpaycreationApi = process.env.REACT_APP_CREATE_RAZORPAY_ORDER;
  const razorpaykeyApi = process.env.REACT_APP_RAZORPAY_KEY;

  // Get booking data from location state
  const { 
    name, 
    dob, 
    gender, 
    selectedDate, 
    serviceTitle, 
    prescription, 
    servicePrice,
    phone,
    orderType, 
    diagnosticOrderData,
  } = location.state || {};

  // Helper function to show dialog
  const showDialog = (message, type = "info") => {
    setDialogMessage(message);
    setDialogType(type);
    setDialogOpen(true);
  };

  // Validate data on mount
  useEffect(() => {
    if (!name || !selectedDate || !serviceTitle || !servicePrice) {
      showDialog("Missing booking information. Please start from the beginning.", "error");
      setTimeout(() => navigate(-1), 2000);
    }
  }, [name, selectedDate, serviceTitle, servicePrice, navigate]);

  const clearBookingFormData = () => {
    try {
      sessionStorage.removeItem("commonFormData");
      console.log(" Booking form data cleared successfully");
    } catch (error) {
      console.error("Error clearing form data:", error);
    }
  };

  // Fetch subscription benefits
  useEffect(() => {
    const fetchSubscriptionBenefits = async () => {
      const userId = sessionStorage.getItem("userId");
      const token = sessionStorage.getItem("token");
      
      if (!userId || !token) return;

      const apiBaseUrl = process.env.REACT_APP_PRICING_API;
      if (!apiBaseUrl) {
        console.error("REACT_APP_PRICING_API is not configured");
        return;
      }

      setLoadingSubscription(true);
      try {
        const url = `${apiBaseUrl}/check-benefits/${userId}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          console.error("API returned non-JSON response");
          return;
        }

        if (!response.ok) return;

        const data = await response.json();
        if (data.success && data.has_subscription) {
          setSubscriptionBenefits(data);
        }
      } catch (error) {
        console.error("Error fetching subscription benefits:", error);
      } finally {
        setLoadingSubscription(false);
      }
    };

    fetchSubscriptionBenefits();
  }, []);

  // Calculate subscription discount
  useEffect(() => {
    if (subscriptionBenefits && subscriptionBenefits.has_subscription) {
      let discountPercent = 0;
      
      if (orderType === "diagnostic") {
        discountPercent = subscriptionBenefits.benefits.diagnostic_discount || 0;
      } else {
        discountPercent = subscriptionBenefits.benefits.service_discount || 0;
      }
      
      const discountAmount = (servicePrice * discountPercent) / 100;
      setSubscriptionDiscount(discountAmount);
    } else {
      setSubscriptionDiscount(0);
    }
  }, [servicePrice, subscriptionBenefits, orderType]);

  // Fetch available promo codes
  useEffect(() => {
    fetchAvailablePromos();
  }, [orderType]);

  const fetchAvailablePromos = async () => {
    try {
      const userId = sessionStorage.getItem("userId");
      const token = sessionStorage.getItem("token");
      
      const serviceType = orderType === "diagnostic" ? "diagnostic" : "service";
      
      const response = await fetch(
        `${process.env.REACT_APP_APPLY_PROMOTION_API}/available?service_type=${serviceType}&user_id=${userId}`,
        {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAvailablePromos(data.promotions || []);
        }
      }
    } catch (error) {
      console.error("Error fetching promos:", error);
    }
  };

  const applyPromoCode = async (code) => {
    setPromoLoading(true);
    try {
      const userId = sessionStorage.getItem("userId");
      const token = sessionStorage.getItem("token");
      
      const serviceType = orderType === "diagnostic" ? "diagnostic" : "service";
      
      const response = await fetch(
        `${process.env.REACT_APP_APPLY_PROMOTION_API}/apply`,
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            promo_code: code.toUpperCase(),
            service_type: serviceType,
            order_price: servicePrice,
            user_id: userId,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        setAppliedPromo({
          code: data.promo_code,
          discount: data.discount_amount,
          title: data.title,
        });
        setPromoCode("");
        setShowAvailablePromos(false);
        showDialog(data.message || "Promo code applied successfully!", "success");
      } else {
        showDialog(data.error || "Invalid promo code", "error");
      }
    } catch (error) {
      console.error("Error applying promo:", error);
      showDialog("Failed to apply promo code. Please try again.", "error");
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
    showDialog("Promo code removed", "info");
  };

  const applySubscriptionToOrder = async () => {
    if (!subscriptionBenefits || !subscriptionBenefits.has_subscription) {
      return null;
    }

    const userId = sessionStorage.getItem("userId");
    const token = sessionStorage.getItem("token");
    
    const apiBaseUrl = process.env.REACT_APP_PRICING_API;
    if (!apiBaseUrl) return null;

    try {
      const url = `${apiBaseUrl}/apply-subscription`;
      const serviceType = orderType === "diagnostic" ? "diagnostic" : "service";
      
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          user_id: userId,
          service_type: serviceType,
          base_price: servicePrice
        })
      });

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        return null;
      }

      if (!response.ok) return null;

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error applying subscription:", error);
      return null;
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = process.env.REACT_APP_RAZORPAY_SCRIPT;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const createBooking = async (paymentData) => {
    const storedUser = JSON.parse(sessionStorage.getItem("user")) || {};

    const cleanPaymentData = {
      ...paymentData,
      amount: typeof paymentData.amount === 'string' 
        ? parseFloat(paymentData.amount.toString().replace(/[^0-9.]/g, ''))
        : paymentData.amount
    };

    // Apply subscription to backend
    const subscriptionResponse = await applySubscriptionToOrder();
    const diagnosticTestsString =
    orderType === "diagnostic"
      ? diagnosticOrderData?.tests
          ?.map(t => t.test_name)
          .join(", ")
      : "";
  
    const bookingPayload = {
      name,
      email: storedUser.email,
      phone: phone || storedUser.phone,
      gender,
      age: dob,
      service_type: serviceTitle,
      date: selectedDate,
      diagnostic_tests: diagnosticTestsString,
      notes: orderType === "diagnostic"
        ? "Booked through diagnostic services"
        : "Booked through the services page",

      // notes: orderType === "diagnostic" 
      //   ? `Diagnostic tests: ${diagnosticOrderData?.tests?.map(t => t.test_name).join(', ')}` 
      //   : "Booked through the services page",
      source: "webApp",
      payment: cleanPaymentData,
      prescription,
      // ✅ Always include discount fields (even if 0)
      subscription_discount: subscriptionDiscount || 0,
      promo_discount: appliedPromo ? appliedPromo.discount : 0,
      promo_code: appliedPromo ? appliedPromo.code : null,
      subscription_id: subscriptionBenefits?.subscription?.subscription_id || null,
      ...(orderType === "diagnostic" && {
        order_type: "diagnostic",
        // diagnostic_tests: diagnosticOrderData?.tests || [],
        test_details: diagnosticOrderData,
      }),
    };

    try {
      const res = await fetch(bookAppointmentApi, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bookingPayload),
      });

      const result = await res.json();
      
      if (!res.ok) {
        throw new Error(result.error || result.detail || "Booking failed");
      }

      // CRITICAL: Clear form data after successful booking
      clearBookingFormData();
      
      navigate("/booking-status", {
        state: { 
          ...result, 
          name, 
          serviceTitle, 
          selectedDate,
          paymentMethod: cleanPaymentData.method
        },
      });

    } catch (err) {
      console.error("❌ Booking error:", err);
      throw err;
    }
  };

  const handleOfflinePayment = async () => {
    const paymentData = {
      payment_id: `offline_${Date.now()}`,
      amount: finalAmount,
      method: "offline",
      status: "pending",
      gateway: null,
      transaction_history: [
        {
          timestamp: new Date().toISOString(),
          method: "offline",
          status: "pending",
        }
      ],
    };
  
    await createBooking(paymentData);
  };

  const handleOnlinePayment = async () => {
    const storedUser = JSON.parse(sessionStorage.getItem("user")) || {};

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error("Failed to load Razorpay SDK. Please check your internet connection.");
      }

      const orderRes = await fetch(razorpaycreationApi, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          amount: finalAmount,
          currency: "INR",
          receipt: `receipt_${Date.now()}`,
          notes: {
            name: name,
            service: serviceTitle,
            date: selectedDate
          }
        }),
      });

      if (!orderRes.ok) {
        const errorData = await orderRes.json();
        throw new Error(errorData.error || errorData.detail || "Failed to create payment order");
      }

      const orderData = await orderRes.json();

      const options = {
        key: razorpaykeyApi,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Ayuja Services",
        description: `Appointment: ${serviceTitle}`,
        order_id: orderData.id,
        
        prefill: {
          name: storedUser.name || name,
          email: storedUser.email,
          contact: storedUser.phone || phone,
        },
        
        config: {
          display: {
            blocks: {
              banks: {
                name: "All Payment Methods",
                instruments: [
                  { method: "upi", flows: ["qr", "collect", "intent"] },
                  { method: "card" },
                  { method: "netbanking" },
                  { method: "wallet" },
                ],
              },
            },
            sequence: ["block.banks"],
            preferences: { show_default_blocks: true },
          },
        },
        
        theme: { 
          color: "#008080",
          backdrop_color: "rgba(0, 0, 0, 0.5)",
        },
        
        handler: async function (response) {
          await handlePaymentSuccess(response, orderData);
        },
        
        modal: {
          ondismiss: function() {
            showDialog("Payment cancelled. Please try again when ready.", "warning");
            setLoading(false);
          },
          confirm_close: true,
          escape: true,
        },
        
        retry: {
          enabled: true,
          max_count: 3,
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (err) {
      console.error("💥 Online payment error:", err);
      showDialog(err.message || "Payment initialization failed. Please try again.", "error");
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (razorpayResponse, orderData) => {
    try {
      const verifyRes = await fetch(verifyPaymentApi, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          razorpay_order_id: razorpayResponse.razorpay_order_id,
          razorpay_payment_id: razorpayResponse.razorpay_payment_id,
          razorpay_signature: razorpayResponse.razorpay_signature,
          method: "online",
          amount: orderData.amount,
        }),
      });

      if (!verifyRes.ok) {
        const errorData = await verifyRes.json();
        throw new Error(errorData.error || errorData.detail || "Payment verification failed");
      }

      const verifyData = await verifyRes.json();

      if (verifyData.status !== "success") {
        throw new Error(
          verifyData.message || `Payment verification failed. Please contact support with Payment ID: ${razorpayResponse.razorpay_payment_id}`
        );
      }

      const paymentData = {
        payment_id: razorpayResponse.razorpay_payment_id,
        order_id: razorpayResponse.razorpay_order_id,
        method: "online",
        status: "success",
        amount: orderData.amount,
        gateway: "razorpay",
        currency: "INR",
        transaction_history: [
          {
            timestamp: new Date().toISOString(),
            method: "online",
            gateway: "razorpay",
            payment_id: razorpayResponse.razorpay_payment_id,
            order_id: razorpayResponse.razorpay_order_id,
            status: "success",
          }
        ],
      };

      await createBooking(paymentData);

    } catch (err) {
      console.error("💥 Payment verification error:", err);
      showDialog(err.message || "Payment verification failed. Please contact support.", "error");
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    if (!selected) {
      showDialog("Please select a payment method", "warning");
      return;
    }

    setLoading(true);

    try {
      if (selected === "offline") {
        await handleOfflinePayment();
      } else if (selected === "online") {
        await handleOnlinePayment();
      }
    } catch (err) {
      console.error("Error in handleContinue:", err);
      showDialog("An error occurred. Please try again.", "error");
      setLoading(false);
    }
  };

  const promoDiscount = appliedPromo ? appliedPromo.discount : 0;
  const finalAmount = Math.max(0, servicePrice - subscriptionDiscount - promoDiscount);

  return (
    <>
      <Box
        sx={{
          p: 4,
          maxWidth: 600,
          mx: "auto",
          backgroundColor: "#fff",
          mt: 2,
          border: "2px solid #008080",
          borderRadius: 2,
        }}
      >
        <IconButton onClick={() => navigate(-1)} disabled={loading}>
          <ArrowBackIosNewIcon />
        </IconButton>

        <Typography
          variant="h5"
          sx={{ mb: 2, color: "#004d4d", fontWeight: 700 }}
        >
          PAYMENT METHOD
        </Typography>

        <Typography variant="body1" sx={{ mb: 3, color: "#555" }}>
          Choose how you'd like to pay
        </Typography>

        {/* Booking Summary */}
        <Paper 
          sx={{ 
            p: 2, 
            mb: 3, 
            backgroundColor: "#f5f5f5",
            borderLeft: "4px solid #008080" 
          }}
        >
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Booking Summary
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Service:</strong> {serviceTitle}
          </Typography>

          {orderType === "diagnostic" && diagnosticOrderData?.tests && (
            <Box sx={{ mt: 1, mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Selected Tests:
              </Typography>
              {diagnosticOrderData.tests.map((test, index) => (
                <Typography key={index} variant="caption" display="block" sx={{ ml: 1 }}>
                  • {test.test_name} - ₹{test.mrp}
                </Typography>
              ))}
              {diagnosticOrderData.savings > 0 && (
                <Typography variant="caption" color="success.main" sx={{ ml: 1 }}>
                  You save: ₹{diagnosticOrderData.savings.toFixed(2)}
                </Typography>
              )}
            </Box>
          )}

          <Typography variant="body2">
            <strong>Date:</strong> {selectedDate ? new Date(selectedDate).toLocaleString() : 'N/A'}
          </Typography>
          
          <Divider sx={{ my: 1.5 }} />
          
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Base Amount:</strong> ₹{servicePrice}
          </Typography>

          {/* Subscription Discount */}
          {subscriptionBenefits && subscriptionBenefits.has_subscription && subscriptionDiscount > 0 && (
            <Box sx={{ 
              backgroundColor: "#F3E8FF", 
              p: 1.5, 
              borderRadius: 1, 
              mb: 1,
              border: "1px solid #DDD6FE"
            }}>
              <Typography variant="body2" sx={{ color: "#7C3AED", fontWeight: 600, mb: 0.5 }}>
                🎉 Subscription Discount ({
                  orderType === "diagnostic" 
                    ? subscriptionBenefits.benefits.diagnostic_discount 
                    : subscriptionBenefits.benefits.service_discount
                }%)
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                {subscriptionBenefits.subscription.name}
              </Typography>
              <Typography variant="body2" sx={{ color: "#7C3AED", fontWeight: 600, mt: 0.5 }}>
                -₹{subscriptionDiscount.toFixed(2)}
              </Typography>
            </Box>
          )}

          {/* Promo Discount */}
          {appliedPromo && (
            <Typography variant="body2" sx={{ color: "#10B981", mb: 0.5 }}>
              <strong>Promo Discount ({appliedPromo.code}):</strong> -₹{appliedPromo.discount.toFixed(2)}
            </Typography>
          )}

          <Divider sx={{ my: 1 }} />

          <Typography variant="body2" sx={{ fontWeight: 700, color: "#008080", fontSize: 18 }}>
            <strong>Total Amount:</strong> ₹{finalAmount.toFixed(2)}
          </Typography>
        </Paper>

        {/* Promo Code Section */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: "#f9f9f9" }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            <LocalOfferIcon sx={{ fontSize: 18, mr: 1, verticalAlign: "middle" }} />
            Apply Coupon Code
          </Typography>

          {!appliedPromo ? (
            <>
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Enter promo code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  disabled={promoLoading || loading}
                  sx={{ 
                    backgroundColor: "#fff",
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => applyPromoCode(promoCode)}
                  disabled={!promoCode || promoLoading || loading}
                  sx={{
                    backgroundColor: "#008080",
                    "&:hover": { backgroundColor: "#006666" },
                    minWidth: 100,
                    textTransform: "none",
                  }}
                >
                  {promoLoading ? "Applying..." : "Apply"}
                </Button>
              </Box>

              <Button
                fullWidth
                variant="outlined"
                onClick={() => setShowAvailablePromos(!showAvailablePromos)}
                endIcon={showAvailablePromos ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{
                  borderColor: "#008080",
                  color: "#008080",
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#006666",
                    backgroundColor: "#E6F7F7",
                  }
                }}
              >
                <CardGiftcardIcon sx={{ fontSize: 18, mr: 1 }} />
                View Available Offers ({availablePromos.length})
              </Button>

              <Collapse in={showAvailablePromos}>
                <Box sx={{ mt: 2 }}>
                  {availablePromos.length > 0 ? (
                    availablePromos.map((promo, index) => (
                      <Paper
                        key={index}
                        sx={{
                          p: 2,
                          mb: 1.5,
                          border: "1px solid #E5E7EB",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          "&:hover": {
                            borderColor: "#008080",
                            backgroundColor: "#E6F7F7",
                          }
                        }}
                        onClick={() => applyPromoCode(promo.promo_code)}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                          <Box sx={{ flex: 1 }}>
                            <Chip 
                              label={promo.promo_code} 
                              size="small" 
                              sx={{ 
                                backgroundColor: "#008080", 
                                color: "#fff", 
                                fontWeight: 600,
                                mb: 1
                              }} 
                            />
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              {promo.description}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Min order: ₹{promo.min_order_value}
                              {promo.max_discount > 0 && ` • Max discount: ₹${promo.max_discount}`}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="contained"
                            sx={{
                              backgroundColor: "#008080",
                              "&:hover": { backgroundColor: "#006666" },
                              textTransform: "none",
                              minWidth: 70,
                            }}
                          >
                            Apply
                          </Button>
                        </Box>
                      </Paper>
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: "center", py: 2 }}>
                      No offers available at the moment
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </>
          ) : (
            <Box 
              sx={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "space-between",
                backgroundColor: "#D1FAE5",
                p: 2,
                borderRadius: 2,
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#065F46" }}>
                  {appliedPromo.code} Applied
                </Typography>
                <Typography variant="caption" sx={{ color: "#047857" }}>
                  You saved ₹{appliedPromo.discount.toFixed(2)}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={removePromoCode}
                disabled={loading}
                sx={{
                  color: "#065F46",
                  "&:hover": { backgroundColor: "#A7F3D0" }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Paper>

        {/* Payment Options */}
        <Stack spacing={2}>
          {paymentOptions.map((option) => {
            const isSelected = selected === option.id;
            return (
              <Paper
                key={option.id}
                variant="outlined"
                onClick={() => !loading && setSelected(option.id)}
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderColor: isSelected ? "#008080" : "#ccc",
                  backgroundColor: isSelected ? "#E6F7F7" : "#fff",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.6 : 1,
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: loading ? "#ccc" : "#008080",
                    backgroundColor: loading ? (isSelected ? "#E6F7F7" : "#fff") : "#E6F7F7",
                  }
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  {option.icon}
                  <Box>
                    <Typography fontWeight={isSelected ? 600 : 400}>
                      {option.label}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {option.description}
                    </Typography>
                  </Box>
                </Stack>
                <Radio
                  checked={isSelected}
                  value={option.id}
                  disabled={loading}
                  onChange={() => setSelected(option.id)}
                  sx={{
                    color: "#008080",
                    "&.Mui-checked": { color: "#008080" },
                  }}
                />
              </Paper>
            );
          })}
        </Stack>



        <Box display="flex" justifyContent="center" mt={4}>

          <Button
            variant="contained"
            sx={{
              backgroundColor: "#008080",
              "&:hover": { backgroundColor: "#006666" },
              px: 4,
              minWidth: 200,
            }}
            onClick={handleContinue}
            disabled={loading}
          >
            {loading ? (
              <>
                <CircularProgress size={24} sx={{ color: "#fff", mr: 1 }} />
                Processing...
              </>
            ) : (
              "Proceed to Pay"
            )}
          </Button>
        </Box>
      </Box>

      <CustomDialog
        open={dialogOpen}
        message={dialogMessage}
        onClose={() => setDialogOpen(false)}
      />


    </>
  );
};

export default PaymentMethodSelection;